/** Builds {@link AppContainer} wiring (telemetry, HTTP transport, auth, sync, feature shells). */
import { AppConfig } from '@core/domain/AppConfig.ts';
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type { HttpTransportPort } from '@core/domain/ports/driven/HttpTransportPort.ts';
import { applyTenantCssVariables } from '@core/infrastructure/driven/theme/CssThemeApplicator.ts';
import { getTenant, setTenant } from '@core/domain/TenantContext.ts';
import { createHttpClient } from '@core/infrastructure/driven/http/HttpClient.ts';
import type { HttpClient } from '@core/infrastructure/driven/http/HttpClient.ts';
import { createHttpTransport } from '@core/infrastructure/driven/http/createHttpTransport.ts';
import { AuthStorageAdapter } from '@src/modules/auth/infrastructure/driven/AuthStorageAdapter.ts';
import { createAuthApplicationService } from '@src/modules/auth/infrastructure/factories/AuthFactory.ts';
import { PuntosEntregaApplicationService } from '@src/modules/puntos_entrega/application/PuntosEntregaApplicationService.ts';
import type { RedencionBonoApplicationService } from '@src/modules/redencion_bono/application/RedencionBonoApplicationService.ts';
import type { NotificationPort } from '@src/modules/shared/notifications/domain/ports/NotificationPort.ts';
import { createNotificationPort } from '@src/modules/shared/notifications/infrastructure/driven/DomToastNotificationAdapter.ts';
import { SqlJsTelemetryLocalStore } from '@src/modules/shared/observability/telemetry/infrastructure/driven/SqlJsTelemetryLocalStore.ts';
import { createTelemetry } from '@src/modules/shared/observability/telemetry/infrastructure/factories/createTelemetry.ts';
import { createSqlJsObservabilidadPersistedRead } from '@src/modules/observabilidad/infrastructure/driven/sqlJsObservabilidadPersistedRead.ts';
import type { ObservabilidadPersistedReadPort } from '@src/modules/observabilidad/domain/ports/ObservabilidadPersistedReadPort.ts';
import type { CatalogDownloadPort } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import type { OfflineSyncPort } from '@src/modules/sync/domain/ports/OfflineSyncPort.ts';
import type { SyncStorageMetricsPort } from '@src/modules/sync/domain/ports/SyncStorageMetricsPort.ts';
import type { AppMaintenancePort } from '@src/modules/sync/domain/ports/AppMaintenancePort.ts';
import type { AppUpdatePort } from '@src/modules/sync/domain/ports/AppUpdatePort.ts';
import { createSqlJsSyncStorageMetricsPort } from '@src/modules/sync/infrastructure/driven/sqlJsSyncStorageMetrics.ts';
import { AuthService } from '@src/modules/auth/application/AuthService.ts';
import { createDefaultFeatureShellApplications } from './offlineSqliteBootstrap.ts';
import type { AppContainerDeps } from './AppContainer.ts';
import { buildSyncServices } from './appContainerSyncWiring.ts';
import { buildMaintenanceServices } from './appContainerMaintenanceWiring.ts';

export type AssembledAppContainer = {
  httpClient: HttpClient | null;
  httpTransport: HttpTransportPort;
  authStorage: AuthStorageAdapter;
  authService: AuthService;
  notificationPort: NotificationPort;
  telemetry: TelemetryPort;
  puntosEntregaShell: PuntosEntregaApplicationService;
  redencionBonoApplication: RedencionBonoApplicationService;
  syncStorageMetrics: SyncStorageMetricsPort;
  offlineSync: OfflineSyncPort;
  catalogDownload: CatalogDownloadPort;
  observabilidadPersistedRead: ObservabilidadPersistedReadPort;
  appMaintenance: AppMaintenancePort;
  appUpdate: AppUpdatePort;
};

export class AppContainerBuilder {
  constructor(private readonly deps: AppContainerDeps = {}) {}

  /** Wires all composition-root dependencies. */
  assemble(): AssembledAppContainer {
    let tenantConfig = AppConfig.getDefaultTenantConfig();
    try {
      tenantConfig = getTenant();
    } catch {
      setTenant(tenantConfig);
    }
    applyTenantCssVariables(tenantConfig.theme);

    const offline = AppConfig.useLocalSqliteClient();
    const localTelemetryStore = offline
      ? new SqlJsTelemetryLocalStore(
          () => this.deps.localSqlite?.getDb(),
          () => this.deps.localSqlite?.notifyPersist(),
        )
      : undefined;
    const telemetry = createTelemetry({ localStore: localTelemetryStore });
    const authStorage = new AuthStorageAdapter(AppConfig.getTokenKey());
    const httpTransport = createHttpTransport('xhr');
    const httpClient = offline ? null : createHttpClient(authStorage, telemetry, createHttpTransport('fetch'));

    const authService = createAuthApplicationService(
      authStorage,
      offline ? null : httpClient,
      offline,
      offline ? () => this.deps.localSqlite?.getDb() : undefined,
      offline ? () => this.deps.localSqlite?.getConnectionHealth() : undefined,
    );

    const notificationPort = createNotificationPort();

    const defaults = createDefaultFeatureShellApplications();
    const puntosEntregaShell = this.deps.puntosEntregaShell ?? defaults.puntosEntregaShell;
    const redencionBonoApplication = this.deps.redencionBonoApplication ?? defaults.redencionBonoApplication;
    const syncStorageMetrics = createSqlJsSyncStorageMetricsPort(this.deps.localSqlite);

    const { offlineSync, catalogDownload } = buildSyncServices({
      deps: this.deps,
      authStorage,
      httpTransport,
      puntosEntregaShell,
      redencionBonoApplication,
    });

    const observabilidadPersistedRead = createSqlJsObservabilidadPersistedRead(this.deps.localSqlite);

    const { appMaintenance, appUpdate } = buildMaintenanceServices({
      localSqlite: this.deps.localSqlite,
      authStorage,
      pendingUploadGuard: () => offlineSync.getExportRecordCounts(),
    });

    return {
      httpClient,
      httpTransport,
      authStorage,
      authService,
      notificationPort,
      telemetry,
      puntosEntregaShell,
      redencionBonoApplication,
      syncStorageMetrics,
      offlineSync,
      catalogDownload,
      observabilidadPersistedRead,
      appMaintenance,
      appUpdate,
    };
  }

  /** @deprecated Prefer {@link AppContainer.create}. */
  build(): AssembledAppContainer {
    return this.assemble();
  }
}
